import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import './PricingCalendar.css';

const PricingCalendar = ({ 
  basePrice, 
  businessHours, 
  priceVariations = [], 
  onAddVariation, 
  onEditVariation, 
  onRemoveVariation 
}) => {
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingVariation, setEditingVariation] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  
  // Ref para el contenedor principal del calendario
  const calendarRef = useRef(null);

  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      const isMobileView = window.innerWidth <= 768;
      setIsMobile(isMobileView);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Detectar scroll del contenedor principal (opcional, para debugging)
  useEffect(() => {
    const container = calendarRef.current;
    if (!container) return;

    // Log simple para verificar dimensiones
    console.log('📏 Container scrollWidth:', container.scrollWidth, 'clientWidth:', container.clientWidth);
    console.log('📏 Scroll disponible:', container.scrollWidth > container.clientWidth ? 'SÍ' : 'NO');
    console.log('📏 Diferencia:', container.scrollWidth - container.clientWidth, 'px');

    const handleContainerScroll = () => {
      const scrollPercent = Math.round((container.scrollLeft / (container.scrollWidth - container.clientWidth)) * 100);
      console.log('🔄 Scroll del contenedor:', container.scrollLeft, `(${scrollPercent}%)`);
    };

    if (container.scrollWidth > container.clientWidth) {
      container.addEventListener('scroll', handleContainerScroll);
      console.log('✅ Scroll habilitado - Ancho disponible:', container.clientWidth, 'Ancho contenido:', container.scrollWidth);
      console.log('✅ Scroll máximo:', container.scrollWidth - container.clientWidth, 'px');
    } else {
      console.log('❌ No hay scroll disponible - Todo cabe en pantalla');
    }

    return () => {
      container.removeEventListener('scroll', handleContainerScroll);
    };
  }, []);

  // Generar todas las horas del día (siempre estable)
  const allHours = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return `${hour}:00`;
  });
  
  // Filtrar las horas que están en el horario del negocio
  const getAvailableHours = () => {
    const hoursSet = new Set();
    
    if (businessHours) {
      Object.values(businessHours).forEach(daySchedule => {
        if (daySchedule.enabled && daySchedule.ranges) {
          daySchedule.ranges.forEach(range => {
            const startHour = parseInt(range.start.split(':')[0]);
            const endHour = parseInt(range.end.split(':')[0]);
            
            for (let h = startHour; h < endHour; h++) {
              hoursSet.add(h);
            }
          });
        }
      });
    }
    
    // Si no hay horarios definidos, mostrar horarios típicos de negocio
    if (hoursSet.size === 0) {
      for (let h = 9; h < 18; h++) {
        hoursSet.add(h);
      }
    }
    
    return hoursSet;
  };
  
  const availableHoursSet = getAvailableHours();
  
  // Usar todas las horas pero mostrar solo las disponibles
  const hours = allHours;

  // Días de la semana con mapeo correcto
  const daysOfWeek = [
    { id: 'monday', name: 'Lunes', short: 'LUN' },
    { id: 'tuesday', name: 'Martes', short: 'MAR' },
    { id: 'wednesday', name: 'Miércoles', short: 'MIÉ' },
    { id: 'thursday', name: 'Jueves', short: 'JUE' },
    { id: 'friday', name: 'Viernes', short: 'VIE' },
    { id: 'saturday', name: 'Sábado', short: 'SÁB' },
    { id: 'sunday', name: 'Domingo', short: 'DOM' }
  ];

  // Verificar si un slot está dentro del horario base del negocio
  const isInBusinessHours = (dayId, hour) => {
    const daySchedule = businessHours?.[dayId];
    if (!daySchedule || !daySchedule.enabled) return false;
    
    const hourNum = parseInt(hour.split(':')[0]);
    
    // Verificar si esta hora está en el set de horas disponibles
    if (!availableHoursSet.has(hourNum)) return false;
    
    // Verificar cada rango de horario del día
    return daySchedule.ranges?.some(range => {
      const startHour = parseInt(range.start.split(':')[0]);
      const endHour = parseInt(range.end.split(':')[0]);
      return hourNum >= startHour && hourNum < endHour;
    }) || false;
  };

  // Obtener variación de precio para un slot específico
  const getSlotPriceVariation = (dayId, hour) => {
    const hourNum = parseInt(hour.split(':')[0]);
    
    // Buscar en orden inverso para que las últimas variaciones tengan prioridad
    for (let i = priceVariations.length - 1; i >= 0; i--) {
      const variation = priceVariations[i];
      const variationDays = variation.daysOfWeek || variation.days || [];
      if (!variationDays.includes(dayId)) continue;
      
      for (const range of variation.timeRanges || []) {
        const startHour = parseInt(range.start.split(':')[0]);
        const endHour = parseInt(range.end.split(':')[0]);
        
        if (hourNum >= startHour && hourNum < endHour) {
          return variation;
        }
      }
    }
    return null;
  };

  // Obtener el precio y estado de un slot
  const getSlotInfo = (dayId, hour) => {
    const inBusinessHours = isInBusinessHours(dayId, hour);
    const variation = getSlotPriceVariation(dayId, hour);
    
    return {
      isActive: inBusinessHours,
      price: variation ? variation.price : (inBusinessHours ? basePrice : null),
      variation: variation,
      isBase: inBusinessHours && !variation
    };
  };

  // Manejar el inicio de selección
  const handleMouseDown = (dayId, hour) => {
    const slotInfo = getSlotInfo(dayId, hour);
    if (!slotInfo.isActive) {
      return;
    }
    
    setIsSelecting(true);
    setSelectedSlots([{ dayId, hour }]);
  };

  // Manejar el movimiento del mouse durante la selección
  const handleMouseEnter = (dayId, hour) => {
    if (!isSelecting) return;
    
    const slotInfo = getSlotInfo(dayId, hour);
    if (!slotInfo.isActive) return;
    
    setSelectedSlots(prev => {
      const exists = prev.some(s => s.dayId === dayId && s.hour === hour);
      if (!exists) {
        return [...prev, { dayId, hour }];
      }
      return prev;
    });
  };

  // Manejar el fin de la selección
  const handleMouseUp = () => {
    if (isSelecting && selectedSlots.length > 0) {
      setShowPriceModal(true);
    }
    setIsSelecting(false);
  };

  // Manejar click en un slot con variación existente
  const handleSlotClick = (dayId, hour, variation) => {
    if (!variation || isSelecting) return;
    
    setEditingVariation(variation);
    setNewPrice(variation.price.toString());
    setShowPriceModal(true);
  };

  // Crear nueva variación de precio
  const handleCreateVariation = () => {
    
    if (!newPrice || selectedSlots.length === 0) {
      return;
    }
    
    // Agrupar slots por día
    const dayGroups = {};
    selectedSlots.forEach(slot => {
      if (!dayGroups[slot.dayId]) {
        dayGroups[slot.dayId] = [];
      }
      dayGroups[slot.dayId].push(parseInt(slot.hour.split(':')[0]));
    });
    
    // Crear rangos de tiempo continuos
    const days = Object.keys(dayGroups);
    const timeRanges = [];
    
    // Para simplificar, tomar el rango mínimo y máximo de todas las horas seleccionadas
    const allHours = selectedSlots.map(s => parseInt(s.hour.split(':')[0]));
    const minHour = Math.min(...allHours);
    const maxHour = Math.max(...allHours);
    
    timeRanges.push({
      start: `${minHour.toString().padStart(2, '0')}:00`,
      end: `${(maxHour + 1).toString().padStart(2, '0')}:00`
    });
    
    // 🔥 NUEVA LÓGICA: Verificar si ya existe una variación para estos días y horas
    const existingVariation = priceVariations.find(variation => {
      const variationDays = variation.daysOfWeek || variation.days || [];
      const variationRanges = variation.timeRanges || [];
      
      // Verificar si los días coinciden exactamente
      const daysMatch = days.length === variationDays.length && 
                       days.every(day => variationDays.includes(day));
      
      // Verificar si los rangos de tiempo coinciden
      const rangesMatch = variationRanges.length === 1 && 
                         variationRanges[0].start === timeRanges[0].start &&
                         variationRanges[0].end === timeRanges[0].end;
      
      return daysMatch && rangesMatch;
    });

    if (existingVariation) {
      // 🔄 ACTUALIZAR variación existente
      onEditVariation(existingVariation.id, {
        ...existingVariation,
        price: parseFloat(newPrice),
        daysOfWeek: existingVariation.days || existingVariation.daysOfWeek // Asegurar que existe daysOfWeek
      });
    } else {
      // ✨ CREAR nueva variación
    const newVariation = {
      id: Date.now(),
      name: `Precio especial ${days.map(d => daysOfWeek.find(day => day.id === d)?.short).join(', ')}`,
      price: parseFloat(newPrice),
        days: days, // Mantener para compatibilidad con el componente
        daysOfWeek: days, // Agregar para el cálculo de precios
      timeRanges: timeRanges
    };
    
    onAddVariation(newVariation);
    }
    
    handleCloseModal();
  };

  // Actualizar variación existente
  const handleUpdateVariation = () => {
    if (!editingVariation || !newPrice) return;
    
    onEditVariation(editingVariation.id, {
      ...editingVariation,
      price: parseFloat(newPrice),
      daysOfWeek: editingVariation.days || editingVariation.daysOfWeek // Asegurar que existe daysOfWeek
    });
    
    handleCloseModal();
  };

  // Eliminar variación
  const handleDeleteVariation = () => {
    if (!editingVariation) return;
    
    onRemoveVariation(editingVariation.id);
    handleCloseModal();
  };

  // Cerrar modal y limpiar estado
  const handleCloseModal = () => {
    setShowPriceModal(false);
    setSelectedSlots([]);
    setEditingVariation(null);
    setNewPrice('');
  };

  // Verificar si un slot está seleccionado
  const isSlotSelected = (dayId, hour) => {
    return selectedSlots.some(s => s.dayId === dayId && s.hour === hour);
  };

  // Verificar si hay horarios base definidos
  const hasBusinessHours = businessHours && Object.values(businessHours).some(day => day.enabled && day.ranges?.length > 0);

  return (
    <div className="precios-calendario" ref={calendarRef}>
      {!hasBusinessHours && (
        <div className="precios-calendario__aviso">
          <AlertTriangle size={18} aria-hidden="true" />
          <p>Todavía no cargaste horarios de atención. Configuralos en el paso anterior para poder ponerle otro precio a un día o a una hora.</p>
        </div>
      )}

      <div className="precios-calendario__leyenda">
        <span className="precios-calendario__leyenda-item">
          <span className="precios-calendario__muestra es-base" aria-hidden="true" />
          Precio base (${basePrice})
        </span>
        <span className="precios-calendario__leyenda-item">
          <span className="precios-calendario__muestra es-dinamica" aria-hidden="true" />
          Precio propio
        </span>
        <span className="precios-calendario__leyenda-item">
          <span className="precios-calendario__muestra esta-cerrada" aria-hidden="true" />
          Cerrado
        </span>
      </div>

      {/* Sin el emoji y sin "en azul": las celdas ya no son azules —el calendario
          usa el color de la marca— y una bombita al lado de cada aviso no es
          como se ven las ayudas en el resto de la app. El texto tampoco nombra
          el mouse en pantallas táctiles. */}
      {hasBusinessHours && priceVariations.length === 0 && (
        <p className="precios-calendario__ayuda">
          {isMobile
            ? 'Deslizá para ver el resto de los días y horarios.'
            : 'Arrastrá sobre los horarios disponibles para ponerles otro precio.'}
        </p>
      )}

      <div
        className={`precios-grilla ${isSelecting ? 'es-arrastre' : ''}`}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div className="precios-grilla__cabecera">
          <div className="precios-grilla__esquina">Hora</div>
          {daysOfWeek.map(day => (
            <div key={day.id} className="precios-grilla__dia">
              {/* Los dos nombres van siempre en el DOM y el CSS elige cuál se
                  ve según el ancho. Antes el nombre corto dependía de `isMobile`,
                  que se calcula con un listener de resize: en el primer pintado
                  siempre decía "escritorio", así que en un teléfono la cabecera
                  aparecía con los nombres largos y se acomodaba después. */}
              <span className="precios-grilla__dia-largo">{day.name}</span>
              <span className="precios-grilla__dia-corto">{day.short}</span>
            </div>
          ))}
        </div>

        <div className="precios-grilla__cuerpo">
          {hours.filter(hour => {
            const hourNum = parseInt(hour.split(':')[0]);
            return availableHoursSet.has(hourNum);
          }).map(hour => (
            <div key={hour} className="precios-grilla__fila">
              <div className="precios-grilla__hora">{hour}</div>
              {daysOfWeek.map(day => {
                const slotInfo = getSlotInfo(day.id, hour);
                const isSelected = isSlotSelected(day.id, hour);

                return (
                  <div
                    key={`${day.id}-${hour}`}
                    className={`precios-celda ${
                      !slotInfo.isActive ? 'esta-cerrada' :
                      slotInfo.variation ? 'es-dinamica' :
                      slotInfo.isBase ? 'es-base' : ''
                    } ${isSelected ? 'se-elige' : ''}`}
                    onMouseDown={() => handleMouseDown(day.id, hour)}
                    onMouseEnter={() => handleMouseEnter(day.id, hour)}
                    onClick={() => handleSlotClick(day.id, hour, slotInfo.variation)}
                  >
                    {slotInfo.isActive && (
                      <span className="precios-celda__monto">${slotInfo.price}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Modal para configurar precio */}
      {showPriceModal && (
        <div className="precios-modal-fondo" onClick={handleCloseModal}>
          <div className="precios-modal" onClick={e => e.stopPropagation()}>
            <h3>{editingVariation ? 'Editar precio' : 'Nuevo precio'}</h3>

            <div className="precios-modal__campo">
              <label htmlFor="precio-dinamico">Precio</label>
              <input
                id="precio-dinamico"
                type="number"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                placeholder={`Base: $${basePrice}`}
                autoFocus
              />
            </div>

            {editingVariation && (
              <div className="precios-modal__detalle">
                <p>Días: {editingVariation.days.map(d =>
                  daysOfWeek.find(day => day.id === d)?.name
                ).join(', ')}</p>
                <p>Horarios: {editingVariation.timeRanges.map(r =>
                  `${r.start} a ${r.end}`
                ).join(', ')}</p>
              </div>
            )}

            <div className="precios-modal__acciones">
              <button
                type="button"
                className="precios-modal__boton-cancelar"
                onClick={handleCloseModal}
              >
                Cancelar
              </button>
              {editingVariation && (
                <button
                  type="button"
                  className="precios-modal__boton-eliminar"
                  onClick={handleDeleteVariation}
                >
                  Eliminar
                </button>
              )}
              <button
                type="button"
                className="precios-modal__boton-guardar"
                onClick={() => {
                  if (editingVariation) {
                    handleUpdateVariation();
                  } else {
                    handleCreateVariation();
                  }
                }}
                disabled={!newPrice}
              >
                {editingVariation ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingCalendar; 